import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Star, Calendar, Utensils, Heart } from 'lucide-react';
import { Link } from 'react-router-dom';
import { data } from '../data';

const Barnedaab = () => {
  const service = data.services[2]; // Barnedåb data

  return (
    <div className="pt-0">
      {/* Hero Section */}
      <section className="relative h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src={service.image} 
            className="w-full h-full object-cover" 
            alt="Barnedåb på GuldBoSund Konference & Event" 
          />
          <div className="absolute inset-0 bg-[#081f32]/40" />
        </div>
        
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mt-20">
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2 }}
            className="text-5xl md:text-7xl font-light mb-6 uppercase tracking-wider"
          >
            Barnedåb
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.2 }}
            className="text-xl md:text-2xl font-light"
          >
            En tryg og hjertevarm start på livet i smukke rammer
          </motion.p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl font-light text-gray-900 mb-8 uppercase tracking-wider">Fejr den lille ny med dem du holder af</h2>
              <div className="prose prose-lg text-gray-600 space-y-6">
                <p>
                  En barnedåb eller navngivningsfest er en af livets mest dyrebare øjeblikke. Hos GuldBoSund Konference & Event skaber vi de helt rigtige rammer for en rolig og hyggelig dag, hvor der er plads til både nærvær og fejring.
                </p>
                <p>
                  Vores lokaler er lyse og rummelige med god plads til barnevogne og legende børn. Vi sørger for en lækker brunch eller frokostbuffet, så I kan slappe af og nyde dagen med jeres gæster og den lille hovedperson.
                </p>
              </div>

              <div className="mt-10">
                <p className="text-xs font-medium uppercase tracking-[0.24em] text-[#081f32]/60 mb-3">Personligt tilbud</p>
                <h3 className="text-2xl md:text-3xl font-medium leading-snug text-[#081f32]">
                  Vi skræddersyer og laver gerne et tilbud helt efter jeres ønsker og behov.
                </h3>
              </div>

              <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-6">
                {service.features.map((feature, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle2 className="text-[#dab256] flex-shrink-0" size={20} />
                    <span className="text-gray-700 font-light">{feature}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="bg-[#f8f9fa] p-10 rounded-3xl border border-gray-100 shadow-xl relative overflow-hidden"
            >
              <div className="relative z-10">
                <h3 className="text-2xl font-light text-[#081f32] mb-6 uppercase tracking-widest">Barnedåbspakke</h3>
                <div className="flex items-baseline gap-2 mb-8">
                  <span className="text-5xl font-light text-[#081f32]">{service.price}</span>
                  <span className="text-gray-500 font-light">kr. pr. kuvert</span>
                </div>
                
                <ul className="space-y-6 mb-10 border-t border-gray-200 pt-8">
                  {[
                    { icon: Heart, title: "Velkomstdrink", desc: "Mousserende vin eller hyldeblomst" },
                    { icon: Utensils, title: "Luksus Brunch", desc: "Eller let frokostanretning" },
                    { icon: Calendar, title: "5 timers fest", desc: "Ideelt til formiddags- eller eftermiddagsarrangement" },
                    { icon: Star, title: "Eget lokale", desc: "Med plads til puslebord og barnevogne" }
                  ].map((item, i) => (
                    <li key={i} className="flex gap-4">
                      <div className="w-10 h-10 rounded-full bg-[#081f32]/5 flex items-center justify-center flex-shrink-0">
                        <item.icon className="text-[#081f32]" size={18} />
                      </div>
                      <div>
                        <p className="font-medium text-[#081f32]">{item.title}</p>
                        <p className="text-sm text-gray-500">{item.desc}</p>
                      </div>
                    </li>
                  ))}
                </ul>

                <Link to="/kontakt" className="block w-full bg-[#081f32] text-white py-4 rounded-xl text-center text-xs font-light uppercase tracking-widest hover:bg-[#112d4a] transition-colors shadow-lg">
                  Hør mere om ledige datoer
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Barnedaab;
